import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useLocalStorage } from 'usehooks-ts';
import {
  Task,
  TaskScopeNameEnum,
  TaskTimeline,
  TaskTimelineCompletionGroupByEnum,
  TaskTimelineFrameEnum,
  TaskTimelineReportCreateMutationResult,
  useTaskTimelineReportCreateMutation,
} from 'graphql-common';
import useSearchParam from '@lib/hooks/useSearchParam';
import { TaskCounter } from '@lib/interfaces/task';
import { ErrorLike } from '@apollo/client';
import {
  DASHBOARD_TIMELINE_FILTER_FORM,
  GENERATE_REPORT_FORM,
} from '@lib/enums/localStorageKeys';
import { DEFAULT_LIST_PARAMS, GetUrlParams, ListParams } from '@lib/enums/urls';
import {
  getCetDayRangeBounds,
  getFormattedDate,
} from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { Value, Values } from '@lib/interfaces/form';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import useDashboardTimelineTasksQueryHook from 'utils/fetch/hooks/useDashboardTimelineTasksQueryHook';
import useDashboardTasksCompletionsQueryHook from 'utils/fetch/hooks/useDashboardTasksCompletionsQueryHook';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { FieldNames } from 'components/GenerateReportForm/types';
import useGenerateReportActions from './useGenerateReportActions';
import { getTimelineWindow } from './getPeriodDate';
import { TASKS_TIMELINE_FILTER_EMPTY_VALUES } from './useFilterFields';

export type UseActionsResult = {
  isReportPreparing: boolean;
  reportDefaultPeriod: string[];
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  endDateParam: string | null | undefined;
  filterParams: Values;
  onEndDateChange: (v: string) => void;
  onReportCreateAction: (values: Values) => void;
  onUpdateFilterParams: (values: Values) => void;
  onViewChange: (v: unknown) => void;
  // Pagination and ordering share one local state, so the Table drives
  // both through it (`paginationState` + `orderingState`).
  paginationState: [ListParams, Dispatch<SetStateAction<ListParams>>];
  periodEndParam: string | null | undefined;
  periodStartParam: string | null | undefined;
  resetAllFilterParams: () => void;
  resetFilterParams: () => void;
  taskCounterData: TaskCounter;
  taskTimelineReportCreateMutationResult: TaskTimelineReportCreateMutationResult;
  tasks: Task[];
  tasksError?: ErrorLike;
  tasksFirstLoading: boolean;
  tasksLoading: boolean;
  timelineData?: TaskTimeline[];
  timelineGroupBy?: TaskTimelineFrameEnum;
  timelineLoading: boolean;
  totalTasksCount: number;
  view: TaskTimelineFrameEnum;
};

function getDataFromFilterParams(
  filterParams: Values,
  companyCreatedAt: string,
) {
  const view = (filterParams[TasksFilterFieldNames.TimelineView] ||
    TASKS_TIMELINE_FILTER_EMPTY_VALUES[
      TasksFilterFieldNames.TimelineView
    ]) as TaskTimelineFrameEnum;
  // Company creation date through now: the range the timeline can navigate
  // and the report form can pick from — not what the rows are filtered by.
  const basePeriodStartParam = companyCreatedAt;
  const basePeriodEndParam = new Date().toISOString();
  const scopeName = (filterParams[TasksFilterFieldNames.ScopeName] ||
    TaskScopeNameEnum.DashboardAllGrouped) as string;
  const periodStartParam = getFormattedDate(
    basePeriodStartParam,
    dateFormatForApi,
  );
  const periodEndParam = getFormattedDate(basePeriodEndParam, dateFormatForApi);
  // Falls back to `basePeriodEndParam` (CET-anchored, revertTransformDate-safe),
  // not `periodEndParam` (a bare `yyyy-MM-dd` API date) — `endDateParam` feeds
  // the timeline DatePicker/date math, which parses via `revertTransformDate`.
  const endDateParam =
    (filterParams[TasksFilterFieldNames.TimelineEndDate] as string) ||
    basePeriodEndParam;

  // Rows and stat cards cover the grid's visible window, exactly like the
  // cells (`tasksTimeline`), so every number on screen shares one period.
  const timelineWindow = getTimelineWindow(endDateParam, view);
  const timelineWindowBounds = getCetDayRangeBounds(
    timelineWindow.startDate,
    timelineWindow.endDate,
  );
  const preparedFilters = {
    ...filterParams,
    [TasksFilterFieldNames.Period]: [
      timelineWindowBounds.start,
      timelineWindowBounds.end,
    ],
  };

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    periodEndParam,
    periodStartParam,
    preparedFilters,
    timelineWindowBounds,
    scopeName,
    view,
  };
}

export default function useActions({
  useModalManagementResult,
}: {
  useModalManagementResult: UseModalManagementResult;
}): UseActionsResult {
  const [searchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(DASHBOARD_TIMELINE_FILTER_FORM, TASKS_TIMELINE_FILTER_EMPTY_VALUES);
  const [generateReportForm] = useLocalStorage<{
    [key: string]: Value;
  }>(GENERATE_REPORT_FORM, {});
  const paginationState = useState<ListParams>(DEFAULT_LIST_PARAMS);
  const [paginationParams, setPaginationParams] = paginationState;
  const resetToFirstPage = useCallback(
    () =>
      setPaginationParams((prev) => ({ ...prev, [GetUrlParams.Page]: '1' })),
    [setPaginationParams],
  );

  // Search lives in the URL, not in `paginationState`, so a new query must
  // reset the page here. Adjusted during render so no request goes out with
  // the stale page.
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  if (prevSearchQuery !== searchQuery) {
    setPrevSearchQuery(searchQuery);
    resetToFirstPage();
  }

  // Whoami — reads ProtectedRoute's single query result. See useWhoamiContext.
  const { currentUser } = useWhoamiContext();

  // Data
  const companyCreatedAt = _get(currentUser, 'company.createdAt');
  const {
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    periodEndParam,
    periodStartParam,
    preparedFilters,
    scopeName,
    timelineWindowBounds,
    view,
  } = useMemo(
    () => getDataFromFilterParams(filterParams, companyCreatedAt),
    [filterParams, companyCreatedAt],
  );

  // The report form opens on the grid's visible window.
  const reportDefaultPeriod = useMemo(
    () => [timelineWindowBounds.start, timelineWindowBounds.end],
    [timelineWindowBounds],
  );

  // Tasks
  const {
    taskCounterData,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  } = useDashboardTimelineTasksQueryHook({
    searchQuery,
    filterParams: preparedFilters,
    paginationParams,
    scopeName,
  });
  const {
    loading: timelineLoading,
    data: timelineData,
    dataGroupBy: timelineGroupBy,
  } = useDashboardTasksCompletionsQueryHook({
    tasks,
    filterParams,
    loading: tasksLoading,
  });

  // Reporting
  const [
    taskTimelineReportCreateMutation,
    taskTimelineReportCreateMutationResult,
  ] = useTaskTimelineReportCreateMutation();
  const { onReportCreateAction, isReportPreparing } = useGenerateReportActions({
    onCompleted: () => {
      useModalManagementResult.closeAllModals();
    },
    taskTimelineReportCreateMutation: taskTimelineReportCreateMutation as (
      v: object,
    ) => Promise<unknown>,
    scopeName,
    searchQuery,
    filterParams: preparedFilters,
    completionGroupBy: _get(
      generateReportForm,
      `values.${FieldNames.CompletionGroupBy}.value`,
      TaskTimelineCompletionGroupByEnum.SiteArea,
    ) as TaskTimelineCompletionGroupByEnum,
    basePeriodStartParam,
    basePeriodEndParam,
  });

  const onViewChange = useCallback(
    (option: unknown) => {
      const newView = _get(option, 'value');
      if (newView) {
        // Persist the stored filters, not `preparedFilters` — its Period is
        // derived on every read and must not end up in localStorage.
        setFilterParams({
          ...filterParams,
          [TasksFilterFieldNames.TimelineView]: newView,
          [TasksFilterFieldNames.TimelineEndDate]: basePeriodEndParam,
        });
        resetToFirstPage();
      }
    },
    [setFilterParams, filterParams, basePeriodEndParam, resetToFirstPage],
  );

  const onEndDateChange = useCallback(
    (date: string) => {
      if (date) {
        setFilterParams({
          ...filterParams,
          [TasksFilterFieldNames.TimelineEndDate]: date,
        });
        // Rows are filtered by the visible window, so a new window can have
        // fewer pages than the current one.
        resetToFirstPage();
      }
    },
    [setFilterParams, filterParams, resetToFirstPage],
  );

  const resetFilterParams = useCallback(() => {
    setFilterParams({
      ...TASKS_TIMELINE_FILTER_EMPTY_VALUES,
      ..._pick(filterParams, TasksFilterFieldNames.TimelineEndDate),
    });
    resetToFirstPage();
  }, [filterParams, setFilterParams, resetToFirstPage]);

  const resetAllFilterParams = useCallback(() => {
    setFilterParams(TASKS_TIMELINE_FILTER_EMPTY_VALUES);
    resetToFirstPage();
  }, [setFilterParams, resetToFirstPage]);

  const onUpdateFilterParams = (values: Values) => {
    setFilterParams(values);
    resetToFirstPage();
  };

  return {
    isReportPreparing,
    reportDefaultPeriod,
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    filterParams: preparedFilters,
    onEndDateChange,
    onReportCreateAction,
    onUpdateFilterParams,
    onViewChange,
    paginationState,
    periodEndParam,
    periodStartParam,
    resetAllFilterParams,
    resetFilterParams,
    taskCounterData,
    taskTimelineReportCreateMutationResult,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    timelineData,
    timelineGroupBy,
    timelineLoading,
    totalTasksCount,
    view,
  };
}
